import React from 'react';
import PropTypes from 'prop-types';
import { Card, Col, Descriptions, Row } from 'antd';
import isEmpty from 'lodash/isEmpty';
import { Link } from 'react-router-dom';
import IntlMessages from '../../../../layout/components/lang/IntlMessages';
import urlPageTemplateDetails from '../../../../urls/urlPageTemplateDetails';
import { DATE_TIME_FORMAT, EMPTY_IMAGE_PLACEHOLDER, EMPTY_VALUE_PLACEHOLDER } from '../../../../const/system';
import { HasRights } from '../../../../components/HasRights/HasRights';
import { Permissions } from '../../../../const/permissions';
import useCheckMobileScreen from '../../../../app/hooks/useCheckMobileScreen';
import getFormattedDate from '../../../../lib/getFormattedDate';
import PublishedTag from '../../../../components/PublishedTag';
import SkeletonCardPlaceholder from '../../../../components/SkeletonCardPlaceholder';
import TemplateCollapsibleCards from '../../../../components/TemplateCollapsibleCards';



const CategoryDetailsCard = ({
  data = {},
  isLoadingCard = false,
  isFetching = false,
  dictionary = [],
  languages = [],
  templateCardsData = {},
}) => {
  const isMobile = useCheckMobileScreen(600);
  const canViewTemplate = HasRights([ Permissions.CONSTRUCTORS.TEMPLATES.FIND ]);

  return (
    <Card
      loading={isLoadingCard}
      className="hp-border-color-black-40 hp-card-6 dynamic-details"
      title={data?.title}
    >
      {isFetching ? (
        <SkeletonCardPlaceholder isLoading={isFetching} />
      ) : (
        <Row gutter={[ 24, 24 ]}>
          <Col sm={24} md={6}>
            <img
              alt="preview"
              className="hp-mb-24"
              src={data?.preview ?? EMPTY_IMAGE_PLACEHOLDER}
            />
          </Col>
          <Col sm={24} md={18}>
            <Descriptions
              title={<IntlMessages id='structures-source-category' />}
              layout={isMobile ? 'vertical' : 'horizontal'}
              bordered
              className="hp-mb-32"
              column={1}
            >
              <Descriptions.Item label="ID" className='width-20-vw'>
                <span style={{ wordBreak: 'keep-all' }}>{data?.id}</span>
              </Descriptions.Item>
              <Descriptions.Item label={<IntlMessages id="ui-general-app-id" />}>
                {data?.client_id ?? EMPTY_VALUE_PLACEHOLDER}
              </Descriptions.Item>
              <Descriptions.Item label={<IntlMessages id="ui-general-title" />}>
                {data?.title ?? EMPTY_VALUE_PLACEHOLDER}
              </Descriptions.Item>
              <Descriptions.Item label={<IntlMessages id="ui-general-template" />}>
                {!isEmpty(data?.template?.data?.title) ? (
                  <div>
                    {canViewTemplate ? (
                      <Link to={urlPageTemplateDetails({ templateId: data?.template_id })} target="_blank">
                        {data?.template?.data?.title}
                      </Link>
                    ) : (
                      <div>{data?.template?.data?.title}</div>
                    )}
                  </div>
                ) : EMPTY_VALUE_PLACEHOLDER}
              </Descriptions.Item>
              <Descriptions.Item label={<IntlMessages id="ui-general-published" />}>
                <PublishedTag
                  published={data?.is_published}
                  publishedDate={data?.published_at}
                />
              </Descriptions.Item>
              <Descriptions.Item label={<IntlMessages id="ui-general-created-at" />}>
                {getFormattedDate(data?.created_at, DATE_TIME_FORMAT)}
              </Descriptions.Item>
              <Descriptions.Item label={<IntlMessages id="ui-general-updated-at" />}>
                {getFormattedDate(data?.updated_at, DATE_TIME_FORMAT)}
              </Descriptions.Item>
              {!isEmpty(data?.published_at) && (
                <Descriptions.Item label={<IntlMessages id="ui-general-published-at" />}>
                  {getFormattedDate(data?.published_at, DATE_TIME_FORMAT)}
                </Descriptions.Item>
              )}
              <Descriptions.Item label={<IntlMessages id="ui-general-description" />}>
                {data?.description || EMPTY_VALUE_PLACEHOLDER}
              </Descriptions.Item>
              <Descriptions.Item label={<IntlMessages id="categories-form-field-options" />}>
                {data?.options || EMPTY_VALUE_PLACEHOLDER}
              </Descriptions.Item>
            </Descriptions>

            <TemplateCollapsibleCards
              dictionary={dictionary}
              languages={languages}
              data={templateCardsData}
              targetData={data?.target?.data}
            />
          </Col>
        </Row>
      )}
    </Card>
  );
};


CategoryDetailsCard.propTypes = {
  data: PropTypes.shape({
    title: PropTypes.string,
    preview: PropTypes.string,
    template_id: PropTypes.number,
    template: PropTypes.object,
    description: PropTypes.string,
    options: PropTypes.string,
    id: PropTypes.number,
    client_id: PropTypes.number,
    is_published: PropTypes.bool,
    created_at: PropTypes.string,
    updated_at: PropTypes.string,
    published_at: PropTypes.string,
    target: PropTypes.object,
  }).isRequired,
  isLoadingCard: PropTypes.bool,
  isFetching: PropTypes.bool,
  dictionary: PropTypes.object,
  templateCardsData: PropTypes.object,
  languages: PropTypes.array,
};

export default CategoryDetailsCard;
